import "./PoiOverview.scss";
import ActivitySwitch from "@/components/Common/ActivitySwitch/ActivitySwitch.vue";

export default {
  name: "PoiOverview",
  components: { ActivitySwitch },
  data: () => ({
    config: {},
    statisticData: {},
    options: {
      legend: {
        display: true
      },
      responsive: true,
      maintainAspectRatio: false
    },
    chartStyle: { height: "300px", position: "relative" },
    ignoreUser: false,
    ignoreUserItems: [
      { text: "Meine Aktivitäten", value: 0 },
      { text: "Alle Aktivitäten", value: 1 }
    ]
  }),
  async mounted() {
    try {
      await this.$store.dispatch("getPoiStatistics");
      await this.$store.dispatch("getAllUsers");
      const { counts } = this.statistics ?? {};
      if (!counts) {
        return;
      }

      for (const [key, value] of Object.entries(counts)) {
        const title = this.getCategoryTypeTitlePlural(value.title);
        const activeCount = value.active;
        const inactiveCount = value.inactive;
        const totalCount = value.total;

        this.statisticData[key] = {
          title: title,
          active: activeCount,
          inactive: inactiveCount,
          total: totalCount
        };
      }
    } catch (error) {
      await this.$store.dispatch("createError", error);
    }
    await this.$store.dispatch("getCategoryTypes");
  },
  methods: {
    goTo(path) {
      this.$router.push(path);
    },
    async loadStatistics() {
      await this.$store.dispatch("getPoiStatistics", {
        ignoreUser: this.ignoreUser ? 1 : 0
      });
    },
    getCategoryTypeTitle(type) {
      return this.categoryTypes?.find(cat => cat._id === type)?.title || "";
    },
    getCategoryTypeTitlePlural(type) {
      switch (type) {
        case "5d5bba43ccbbf0110ad199fd":
        case "Produkt":
          return "Produkte";
        case "59af08f217f9ec01009b1617":
        case "POI":
          return "POIs";
        case "6049bc23dcd565001178b270":
        case "Strecke":
          return "Strecken";
        case "606bfec3dcd565001178ba07":
        case "Unterkunft":
          return "Unterkünfte";
        case "67ac9948ee9c22b94033953a":
        case "Messepartner":
          return "Messepartner";
        case "682ad8bf00cfac3c93784238":
        case "Vereine":
          return "Vereine";
        case "68b6a81ce46e77e5da996de5":
        case "Tagung":
          return "Tagungen";
        default:
          console.error(`no case for ${type} defined.`);
          return type;
      }
    },
    getIcon(type) {
      return this.categoryTypes?.find(cat => cat._id === type)?.icon || "";
    },
    getCategoryTypePercentage(type) {
      if (!type || !this.statisticData?.[type]) return 0;
      const data = this.statisticData[type] || null;

      if (!data) {
        return 0;
      }

      let completeDataCount = 0;
      const keys = Object.keys(this.statisticData);
      const percentages = {};

      keys.forEach(key => {
        const total = this.statisticData[key]?.total;
        if (!isNaN(total)) {
          completeDataCount += total;
        }
      });

      if (!completeDataCount) {
        return 0;
      }

      let totalPercentage = 0;
      let maxKey = null;

      keys.forEach(key => {
        const total = this.statisticData[key]?.total || 0;
        const percentage = Math.round((total / completeDataCount) * 100);
        percentages[key] = percentage;
        totalPercentage += percentage;

        if (!maxKey || total > (this.statisticData[maxKey]?.total || 0)) {
          maxKey = key;
        }
      });

      // Ausgleich der Rundungsdifferenz
      const difference = 100 - totalPercentage;
      if (maxKey) {
        percentages[maxKey] += difference;
      }

      return percentages[type] || 0;
    },
    getCatTypePercentageWidth(percentage) {
      // 150px = 100%
      const val = (150 * percentage) / 100;
      if (!isNaN(val)) return val + "px";
      return "0px";
    },
    getUserAvatar(updatedBy) {
      let user = this.users?.find(user => user._id === updatedBy);
      if (!user && this.user?._id === updatedBy) {
        user = this.user;
      }
      return user?.avatar_uri || "";
    }
  },
  computed: {
    statistics() {
      return this.$store.state.poiStatistics;
    },
    user() {
      return this.$store.state.currentUser;
    },
    users() {
      return this.$store.state.users ?? [];
    },
    lastActivities() {
      return this.$store.state.poiStatistics?.last_pois ?? [];
    },
    poisChartTitle() {
      const { counts } = this.statistics;
      if (!counts) {
        return "";
      }

      const total = Object.values(counts).reduce(
        (acc, curr) => acc + curr.total,
        0
      );
      const titles = Object.values(counts).map(obj =>
        this.getCategoryTypeTitlePlural(obj.title)
      );

      const formatter = new Intl.ListFormat("de", {
        style: "long",
        type: "conjunction"
      });

      return `${total} ${formatter.format(titles)}`;
    },
    allPoiCount() {
      const { counts } = this.statistics;
      if (!counts) {
        return 0;
      }

      return Object.values(counts).reduce((acc, curr) => acc + curr.total, 0);
    },
    activeCategoryTypesLabel() {
      return this.activeCategoryTypes.join("/") || "POIs";
    },
    categoryTypes() {
      return this.$store.state?.categoryTypes ?? [];
    },
    activeCategoryTypes() {
      const categoryTypes = this.$store.state?.categoryTypes ?? [];
      if (categoryTypes.length) {
        return categoryTypes.map(categoryType =>
          this.getCategoryTypeTitlePlural(categoryType.title)
        );
      }
      return [];
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isAdmin() {
      return this.currentUser?.scopes?.includes("role:admin");
    },
    showPoiList() {
      return (
        this.isAdmin ||
        this.currentUser?.scopes?.includes("role:poi") ||
        this.currentUser?.scopes?.includes("role:poi_admin") ||
        this.currentUser?.scopes?.includes("role:poi_edit")
      );
    },
    showPoiWidget() {
      return (
        this.isAdmin || this.currentUser?.scopes?.includes("role:poi_widgets")
      );
    },
    showRegion() {
      return (
        this.isAdmin || this.currentUser?.scopes?.includes("role:regions_admin")
      );
    },
    isPoiAdmin() {
      return (
        this.isAdmin || this.currentUser?.scopes?.includes("role:poi_admin")
      );
    }
  }
};
